import React, { useCallback, useEffect, useState, useMemo } from 'react';
import api from '../../../services/api';
import { moeda } from '../../../components/funcoes';
import { useAuth } from '../../../hooks/auth';
import { RelatorioFiltroPeriodo } from '../../../components/RelatorioFiltroPeriodo';
import Chart from 'react-google-charts';
import { Table } from 'rsuite';
import { NotificationManager } from 'react-notifications';
import { CustomButton } from '../../../components/CustomButton';
import { CustomHeaderCell } from '../../../components/CustomHeaderCell';
import { CustomCell } from '../../../components/CustomCell';
import {
  FaCoins,
  FaQrcode,
  FaCreditCard,
  FaWallet,
} from 'react-icons/fa';
import {
  PageWrapper,
  RelatorioPeriodoVendas,
  PageHeader,
  PageTitle,
  DashboardGrid,
  KpiCard,
  KpiLabelRow,
  KpiIconChip,
  KpiLabel,
  KpiValue,
  ChartCard,
  ChartTitleRow,
  LegendRow,
  LegendItem,
  LegendDot,
  TableCard,
} from './styles';

const { Column } = Table;

const formatarData = (data) =>
  data.getFullYear() +
  '-' +
  ('00' + (data.getMonth() + 1)).slice(-2) +
  '-' +
  ('00' + data.getDate()).slice(-2);

const CORES = [
  '#F2C94C',
  '#1BAF7A',
  '#F97316',
  '#2A78D6',
  '#E5484D',
  '#8B72BE',
  '#4F8FA8',
  '#B5651D',
];

const iconeDaForma = (descricao) => {
  const nome = descricao.toLowerCase();

  if (nome.includes('pix')) return <FaQrcode size={11} />;
  if (nome.includes('dinheiro')) return <FaCoins size={11} />;
  if (/cart|cr[eé]d|d[eé]b/.test(nome)) return <FaCreditCard size={11} />;
  return <FaWallet size={11} />;
};

const periodoDoMes = () => {
  const hoje = new Date();

  return {
    inicio: formatarData(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
    fim: formatarData(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)),
  };
};

const VendasFormaPagamento = () => {
  const { id_empresa } = useAuth();
  const [periodo, setPeriodo] = useState(periodoDoMes);
  const [dataInicio, setDataInicio] = useState(periodo.inicio);
  const [dataFim, setDataFim] = useState(periodo.fim);
  const [loading, setLoading] = useState(false);
  const [formas, setFormas] = useState([]);

  const dadosTabela = useMemo(() => {
    const totalGeral = formas.reduce((soma, forma) => soma + forma.total, 0);

    return formas.map((forma, indice) => ({
      id: forma.id,
      name: forma.descricao,
      color: CORES[indice % CORES.length],
      quantidade: forma.quantidade,
      valor: forma.total,
      percentual: totalGeral > 0 ? (forma.total / totalGeral) * 100 : 0,
    }));
  }, [formas]);

  const dadosRosca = useMemo(
    () => dadosTabela.filter((forma) => forma.valor > 0),
    [dadosTabela],
  );

  const dadosGrafico = useMemo(
    () => [
      ['Forma de pagamento', 'Valor'],
      ...dadosRosca.map((forma) => [forma.name, forma.valor]),
    ],
    [dadosRosca],
  );

  const filtrar = useCallback(async () => {
    setLoading(true);
    await api
      .post('pedido/relatoriosGeral/vendasPorFormaPagamento', {
        IdEmpresa: id_empresa,
        DataInicial: periodo.inicio,
        DataFinal: periodo.fim,
      })
      .then((res) => {
        setFormas(res.data.formas);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [periodo, id_empresa]);

  useEffect(() => {
    filtrar();
  }, [filtrar]);

  const buscar = () => {
    if (!dataInicio || !dataFim) {
      const mes = periodoDoMes();
      setDataInicio(mes.inicio);
      setDataFim(mes.fim);
      setPeriodo(mes);
      return;
    }

    if (dataInicio > dataFim) {
      NotificationManager.warning(
        'A data inicial não pode ser maior que a data final.',
        'Aviso',
      );
      return;
    }

    setPeriodo({ inicio: dataInicio, fim: dataFim });
  };

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Vendas por Forma de Pagamento</PageTitle>
        <RelatorioPeriodoVendas style={{ alignItems: 'flex-end', gap: 8 }}>
          <RelatorioFiltroPeriodo
            dataInicio={dataInicio}
            dataFim={dataFim}
            onChangeDataInicio={setDataInicio}
            onChangeDataFim={setDataFim}
          />
          <CustomButton appearance="primary" loading={loading} onClick={buscar}>
            Buscar
          </CustomButton>
        </RelatorioPeriodoVendas>
      </PageHeader>

      <DashboardGrid>
        {dadosTabela.map((forma) => (
          <KpiCard key={forma.id}>
            <KpiLabelRow>
              <KpiIconChip $color={forma.color}>
                {iconeDaForma(forma.name)}
              </KpiIconChip>
              <KpiLabel>{forma.name}</KpiLabel>
            </KpiLabelRow>
            <KpiValue>{moeda(forma.valor)}</KpiValue>
          </KpiCard>
        ))}

        <ChartCard>
          <ChartTitleRow>
            <KpiLabel>Participação por Forma de Pagamento</KpiLabel>
          </ChartTitleRow>
          {dadosRosca.length > 0 && (
            <Chart
              chartLanguage="pt"
              chartType="PieChart"
              width="100%"
              height="220px"
              loader={<div>Carregando gráfico</div>}
              data={dadosGrafico}
              formatters={[
                {
                  type: 'NumberFormat',
                  column: 1,
                  options: {
                    prefix: 'R$ ',
                    decimalSymbol: ',',
                    groupingSymbol: '.',
                    fractionDigits: 2,
                  },
                },
              ]}
              options={{
                pieHole: 0.6,
                colors: dadosRosca.map((forma) => forma.color),
                legend: 'none',
                backgroundColor: 'transparent',
                pieSliceText: 'none',
                pieSliceBorderColor: 'transparent',
                chartArea: { width: '90%', height: '90%' },
              }}
            />
          )}
          <LegendRow>
            {dadosRosca.map((d) => (
              <LegendItem key={d.name}>
                <LegendDot $color={d.color} />
                {d.name}
              </LegendItem>
            ))}
          </LegendRow>
        </ChartCard>

        <TableCard>
          <ChartTitleRow>
            <KpiLabel>Detalhamento por Forma de Pagamento</KpiLabel>
          </ChartTitleRow>
          <Table data={dadosTabela} autoHeight bordered>
            <Column flexGrow={1}>
              <CustomHeaderCell>Forma de Pagamento</CustomHeaderCell>
              <CustomCell dataKey="name" />
            </Column>
            <Column width={100}>
              <CustomHeaderCell>Quantidade</CustomHeaderCell>
              <CustomCell dataKey="quantidade" />
            </Column>
            <Column width={120}>
              <CustomHeaderCell>Valor</CustomHeaderCell>
              <CustomCell>{(rowData) => moeda(rowData.valor)}</CustomCell>
            </Column>
            <Column width={90}>
              <CustomHeaderCell>% do Total</CustomHeaderCell>
              <CustomCell>
                {(rowData) => `${rowData.percentual.toFixed(1)}%`}
              </CustomCell>
            </Column>
          </Table>
        </TableCard>
      </DashboardGrid>
    </PageWrapper>
  );
};

export default VendasFormaPagamento;
